import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  listExternalMemberAttendances,
  listExternalMembersWithoutBooking,
  listInternalStudentAttendances,
  scanAttendanceCode,
  scanBookingAttendanceCode,
} from "../../services/attendanceService";
import { storageUrl } from "../../services/api";
import {
  TOKENS,
  Icon,
  ICONS,
  Avatar,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  btnSecondary,
  Pagination,
  usePagination,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";

// Client-side PDF export — there's no backend "export attendance" route, so
// this just lays out whatever rows are currently on screen (already filtered
// by search/date) into a table and downloads it as a PDF directly in the
// browser. Works the same for a real fetched history table or the live scan
// session log.
const downloadPdf = (filename, title, headers, rows) => {
  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, 21);
  autoTable(doc, {
    startY: 26,
    head: [headers],
    body: rows.map((row) => row.map((cell) => (cell == null || cell === "" ? "—" : String(cell)))),
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: { fillColor: [29, 78, 216] }, // TOKENS.PRIMARY (blue-700)
  });
  doc.save(filename);
};

const memberName = (a) =>
  [a?.member_title, a?.member_initials, a?.member_lastname].filter(Boolean).join(" ") || "—";

const studentName = (a) => [a?.title, a?.name_with_initial].filter(Boolean).join(" ") || "—";

const fmtTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

// ── Visit check-in / check-out / duration (display only) ───────────────────
// Every attendance row (all three history endpoints, and a check-out scan
// response) carries `scanned_at` (check-in) and `scanned_out_at` (check-out,
// null while the visit is still open) as "YYYY-MM-DD HH:MM:SS" in the
// server's local time, plus the backend's own `duration_time` ("HH:MM:SS").
// The duration is computed here from the two timestamps, falling back to
// duration_time only if a timestamp can't be parsed.
const parseServerTime = (value) => {
  if (!value) return null;
  const d = new Date(String(value).trim().replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

const durationTimeToSeconds = (value) => {
  const m = /^(\d+):(\d{2}):(\d{2})$/.exec(String(value || "").trim());
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : null;
};

// 9000 → "2 hours 30 minutes"; seconds are dropped once past a minute.
const formatDuration = (totalSeconds) => {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return null;
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s < 60) return "Less than a minute";
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  return [
    hours ? `${hours} hour${hours === 1 ? "" : "s"}` : null,
    minutes ? `${minutes} minute${minutes === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" ");
};

const fmtClock = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
const fmtDay = (d) => d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Completed visit length in seconds, or null if it can't be determined.
const visitSeconds = (row) => {
  const checkIn = parseServerTime(row?.scanned_at);
  const checkOut = parseServerTime(row?.scanned_out_at);
  if (checkIn && checkOut && checkOut >= checkIn) return (checkOut - checkIn) / 1000;
  return durationTimeToSeconds(row?.duration_time);
};

// One row's visit state relative to `now` (ms):
//   "completed"    — checked out; duration = check-out − check-in
//   "inside"       — open visit that started today; duration counts up live
//   "no_checkout"  — open visit from an earlier day, never closed
//   "not_admitted" — a Rejected walk-in/internal scan, which is never a visit
// Booking-attendance rows have no `status`, so only the walk-in/internal
// tables ever hit "not_admitted".
const visitInfo = (row, now) => {
  const checkIn = parseServerTime(row?.scanned_at);
  const checkOut = parseServerTime(row?.scanned_out_at);
  if (row?.scanned_out_at) {
    return { state: "completed", checkIn, checkOut, seconds: visitSeconds(row) };
  }
  if (row?.status && String(row.status).toLowerCase() !== "confirmed") {
    return { state: "not_admitted", checkIn, checkOut: null, seconds: null };
  }
  if (checkIn && isSameDay(checkIn, new Date(now))) {
    return { state: "inside", checkIn, checkOut: null, seconds: (now - checkIn) / 1000 };
  }
  return { state: "no_checkout", checkIn, checkOut: null, seconds: null };
};

// Plain-text version of the three cells, for the PDF export.
const visitText = (row, now) => {
  const v = visitInfo(row, now);
  const checkIn = v.checkIn ? `${fmtDay(v.checkIn)} ${fmtClock(v.checkIn)}` : row?.scanned_at;
  if (v.state === "completed") {
    return [checkIn, v.checkOut ? fmtClock(v.checkOut) : row.scanned_out_at, formatDuration(v.seconds)];
  }
  if (v.state === "inside") return [checkIn, "Currently inside", `${formatDuration(v.seconds)} so far`];
  if (v.state === "no_checkout") return [checkIn, "No check-out recorded", null];
  return [checkIn, null, null];
};

// Check-in / Check-out / Duration <td>s for a history table row.
function VisitCells({ row, now }) {
  const v = visitInfo(row, now);
  const cell = "py-3 px-3 text-sm text-gray-600 whitespace-nowrap";
  return (
    <>
      <td className={cell}>
        {v.checkIn ? (
          <>
            <p className="text-gray-700">{fmtClock(v.checkIn)}</p>
            <p className="text-xs text-gray-400">{fmtDay(v.checkIn)}</p>
          </>
        ) : (
          row.scanned_at || "—"
        )}
      </td>
      <td className={cell}>
        {v.state === "completed" ? (
          v.checkOut ? (
            <>
              <p className="text-gray-700">{fmtClock(v.checkOut)}</p>
              {v.checkIn && !isSameDay(v.checkIn, v.checkOut) && (
                <p className="text-xs text-gray-400">{fmtDay(v.checkOut)}</p>
              )}
            </>
          ) : (
            row.scanned_out_at
          )
        ) : v.state === "inside" ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Currently inside
          </span>
        ) : v.state === "no_checkout" ? (
          <span className="text-xs text-amber-600">No check-out recorded</span>
        ) : (
          "—"
        )}
      </td>
      <td className={cell}>
        {v.state === "completed" ? (
          <span className="font-semibold" style={{ color: TOKENS.NAVY }}>
            {formatDuration(v.seconds) || "—"}
          </span>
        ) : v.state === "inside" ? (
          <>
            <p className="font-semibold text-green-700">{formatDuration(v.seconds)}</p>
            <p className="text-xs text-gray-400">so far</p>
          </>
        ) : (
          "—"
        )}
      </td>
    </>
  );
}

// ── Scan result → row shape ─────────────────────────────────────────────────
// Normalizes the backend's scan response (see scanAttendanceCode) into the
// bits the session table renders. Field names differ between the two person
// types the backend resolves against (external member vs. internal student),
// and several rejection branches omit the name/detail fields entirely (e.g.
// "member not found" has no member_name to show) — display() falls back to
// "—" for anything missing rather than assuming a shape.
const scanDisplayName = (result) => {
  if (result.type === "internal_student") {
    return [result.title, result.name_with_initial].filter(Boolean).join(" ") || null;
  }
  return result.member_name || null;
};

const scanDisplayDetail = (result) => {
  if (result.type === "internal_student") {
    return [result.faculty, result.year].filter(Boolean).join(" · ") || result.registration_no || null;
  }
  return result.member_code || null;
};

const scanTypeLabel = (type) =>
  type === "external_member" ? "External Member" : type === "internal_student" ? "Internal Student" : "—";

const scanStatusLabel = (status) => {
  switch (status) {
    case "pending":
      return "Pending";
    case "confirmed":
      return "Approved";
    case "rejected":
      return "Rejected";
    case "error":
      return "Failed";
    default:
      return "—";
  }
};

// AttendanceController::scanWithoutBooking returns both `photo_path`
// (storage-relative) and `photo_url` (built by the backend's asset(), so it
// embeds the backend's own APP_URL host, which can differ from the origin the
// SPA talks to and 404). Both go through storageUrl() so the image is always
// served from API_BASE — `photo_path` first, `photo_url` only as a fallback.
const scanPhotoUrl = (result) => storageUrl(result?.photo_path || result?.photo_url);

// Calendar glyph for the "Year" row below — not part of the shared ICONS map
// since nothing else in the app needs it yet.
const CALENDAR_ICON_PATH =
  "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z";

// Per-type detail rows for the popup's info card — [label, value, iconPath,
// redValue?]. Falsy values are dropped so a field a given branch didn't
// return (e.g. Member ID on a rejection, Attendance ID on some rejections)
// just doesn't render a row, rather than the row array assuming one fixed
// shape for both success and rejection.
// Check-out-only rows — only ever populated when scanWithoutBooking() closed
// out an already-open visit (result.event === "out"; see
// AttendanceController::checkOutOpenVisit). Duration is computed from the
// response's scanned_at/scanned_out_at (or its duration_time) via
// visitSeconds(); the backend's pre-formatted `duration` and raw
// duration_minutes are only fallbacks if neither is usable.
const scanCheckOutRows = (result) => {
  if (result.event !== "out") return [];
  const checkIn = parseServerTime(result.scanned_at);
  const checkOut = parseServerTime(result.scanned_out_at);
  return [
    ["Checked In", checkIn ? fmtClock(checkIn) : null, ICONS.clock],
    ["Checked Out", checkOut ? fmtClock(checkOut) : result.scanned_out_at || null, ICONS.clock],
    [
      "Duration",
      formatDuration(visitSeconds(result)) ||
        result.duration ||
        (result.duration_minutes != null ? `${result.duration_minutes}m` : null),
      ICONS.clock,
    ],
  ];
};

// Booking-only rows — populated only by a with-booking check-in.
const scanBookingRows = (result) => [
  ["Booking ID", result.booking_id, ICONS.card],
  ["Facility", result.facility_name, ICONS.facility],
  [
    "Slot",
    result.start_time && result.end_time
      ? `${result.slot_code ? `${result.slot_code} · ` : ""}${result.start_time}–${result.end_time}`
      : null,
    ICONS.clock,
  ],
];

const scanDetailRows = (result) =>
  (result.type === "internal_student"
    ? [
        ["Registration No", result.registration_no, ICONS.card],
        ["Faculty", result.faculty, ICONS.facility],
        ["Year", result.year, CALENDAR_ICON_PATH],
      ]
    : [
        ["Member Code", result.member_code, ICONS.card],
        result.member_id != null
          ? ["Member ID", result.member_id, ICONS.user]
          : ["Membership Status", result.membership_status, ICONS.attendance, true],
        ["Attendance ID", result.attendance_id, ICONS.attendance],
        ...scanBookingRows(result),
        ...scanCheckOutRows(result),
      ]
  ).filter(([, value]) => value !== null && value !== undefined && value !== "");

// ── Scan result popup — sub-pieces ──────────────────────────────────────────
// A plain tinted person silhouette (not the initials-based Avatar used
// elsewhere in the app) — matches the design for a resolved member/student,
// success (green) or rejected (red). Only used when the backend hasn't sent
// a real photo (see scanPhotoUrl below).
function PersonAvatarIcon({ tone }) {
  const green = tone === "green";
  return (
    <div className={`mx-auto mb-3 w-16 h-16 rounded-full flex items-center justify-center ${green ? "bg-emerald-50" : "bg-red-50"}`}>
      <Icon path={ICONS.user} className={`w-9 h-9 ${green ? "text-emerald-300" : "text-red-300"}`} width={1.5} />
    </div>
  );
}

// Same silhouette with a small "x" badge — used when nobody could be
// identified at all (member not found, invalid code, etc.), as opposed to
// PersonAvatarIcon which implies a real person/student record was resolved.
function NotFoundPersonIcon() {
  return (
    <div className="relative mx-auto mb-3 w-16 h-16">
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
        <Icon path={ICONS.user} className="w-9 h-9 text-red-300" width={1.5} />
      </div>
      <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-red-500 ring-2 ring-white flex items-center justify-center">
        <Icon path={ICONS.x} className="w-3.5 h-3.5 text-white" width={2.5} />
      </div>
    </div>
  );
}

// The boxed "Status" / "Scanned Code" callout.
function InfoBox({ label, value, tone }) {
  return (
    <div className={`rounded-xl border p-3 text-center ${tone === "green" ? "bg-emerald-50/60 border-emerald-100" : "bg-red-50/60 border-red-100"}`}>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className={`text-base font-bold ${tone === "green" ? "text-emerald-600" : "text-red-600"}`}>{value}</p>
    </div>
  );
}

// The bordered card of icon+label+value rows (Member Code, Attendance ID, …).
function DetailRowsCard({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-gray-100 divide-y divide-gray-100 text-left">
      {rows.map(([label, value, iconPath, redValue]) => (
        <div key={label} className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5 text-gray-500">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 flex-shrink-0">
              <Icon path={iconPath} className="w-4 h-4" width={1.8} />
            </span>
            <span className="text-sm">{label}</span>
          </div>
          <span className="text-sm font-bold text-right truncate" style={{ color: redValue ? "#dc2626" : TOKENS.NAVY }}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Scan result popup ────────────────────────────────────────────────────
// Shows the outcome of the most recent scan, and stays on screen until the
// operator dismisses it (Close button, click outside), the 3s auto-dismiss
// timer fires, or the next scan comes in and replaces it (see
// ScanAttendanceTab's `resultModal` state, which handleScan overwrites
// unconditionally on every new scan). Renders one of three shapes:
//   - "success"    — a person was resolved and attendance recorded (green).
//   - "resolved"   — a person was resolved but rejected, e.g. bad membership
//                     or an inactive student (red) — same shell as success.
//   - "unresolved" — nobody could be identified at all (member not found,
//                     invalid QR format, validation failure) — shows a
//                     "Scanned Code" box instead of a details card, since
//                     there's no person to show details about.
function ScanResultModal({ result, onClose }) {
  if (!result) return null;

  if (result.phase === "verifying") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-10 text-center border-2 border-gray-100">
          <div className="flex justify-center mb-5">
            <Spinner className="w-12 h-12 text-blue-600" />
          </div>
          <p className="font-bold text-2xl" style={{ color: TOKENS.NAVY }}>
            Verifying…
          </p>
          <p className="text-sm text-gray-400 mt-1.5 font-mono">{result.code}</p>
        </div>
      </div>
    );
  }

  const success = result.status === "confirmed";
  const displayName = scanDisplayName(result);
  // The backend's rejection message is free text, not an error code — "member
  // not found" is common enough (a mistyped/unregistered code) to get its own
  // heading/subtitle instead of the generic "Scan Failed".
  const isMemberNotFound = !success && /member not found/i.test(result.message || "");
  // "resolved" vs "unresolved" is driven by whether the backend actually
  // identified a member/student (has a name) — several rejection branches
  // (invalid QR format, member not found) never get that far.
  const kind = success ? "success" : displayName ? "resolved" : "unresolved";
  const tone = success ? "green" : "red";
  const statusLabel = result.status === "confirmed" ? "Confirmed" : result.status === "rejected" ? "Rejected" : "Failed";

  const title =
    kind === "success"
      ? "Attendance Verified"
      : kind === "unresolved"
      ? isMemberNotFound
        ? "Member Not Found"
        : "Scan Failed"
      : result.membership_status
      ? `Membership ${result.membership_status}`
      : /no confirmed booking/i.test(result.message || "")
      ? "No Booking Found"
      : result.type === "internal_student"
      ? "Membership Invalid"
      : "Scan Failed";

  const subtitle =
    kind === "success"
      ? scanTypeLabel(result.type)
      : kind === "unresolved"
      ? isMemberNotFound
        ? "Member does not exist"
        : result.message || "The code could not be verified."
      : result.message;

  const rows = kind === "unresolved" ? [] : scanDetailRows(result);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-[390px] bg-white rounded-3xl shadow-2xl px-8 py-6 text-center border-2 ${
          tone === "green" ? "border-emerald-100" : "border-red-100"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`mx-auto mb-3 w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
            tone === "green" ? "bg-emerald-500 shadow-emerald-500/30" : "bg-red-500 shadow-red-500/30"
          }`}
        >
          <Icon path={tone === "green" ? ICONS.check : ICONS.x} className="w-8 h-8 text-white" width={2.5} />
        </div>

        <p className={`font-bold text-2xl mb-3 ${tone === "green" ? "text-emerald-600" : "text-red-600"}`}>{title}</p>

        {kind === "unresolved" ? (
          <>
            <NotFoundPersonIcon />
            <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
            <InfoBox label="Scanned Code" value={result.code} tone="red" />
          </>
        ) : (
          <>
            {scanPhotoUrl(result) ? (
              <div className="flex justify-center mb-3">
                <Avatar name={displayName} url={scanPhotoUrl(result)} size={96} />
              </div>
            ) : (
              <PersonAvatarIcon tone={tone} />
            )}
            <p className="font-bold text-lg mb-1" style={{ color: TOKENS.NAVY }}>
              {displayName}
            </p>
            <p className={`text-sm font-semibold mb-4 ${tone === "green" ? "text-emerald-600" : "text-gray-500"}`}>{subtitle}</p>

            <div className="space-y-3">
              <InfoBox label="Status:" value={statusLabel} tone={tone} />
              <DetailRowsCard rows={rows} />
              {kind === "success" && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5 text-left">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0">
                    <Icon path={ICONS.check} className="w-3.5 h-3.5 text-white" width={2.5} />
                  </span>
                  <p className="text-sm text-emerald-700 font-medium">{result.message || "Attendance recorded successfully."}</p>
                </div>
              )}
            </div>
          </>
        )}

        <button
          onClick={onClose}
          className={`mt-4 w-full rounded-2xl py-3 font-bold text-white transition ${
            tone === "green" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
          }`}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── Decorative "ready to scan" QR graphic ───────────────────────────────────
// Purely visual (not a real scannable code) — the actual scan input is the USB
// keyboard-emulating text field, kept functional but hidden behind this panel.
// Noise pattern is generated once with a fixed seed so it's stable across renders.
const QR_NOISE = (() => {
  const blocks = [];
  const cell = 10;
  let seed = 42;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let row = 0; row < 20; row++) {
    for (let col = 0; col < 20; col++) {
      const x = col * cell;
      const y = row * cell;
      const inTopLeft = x < 70 && y < 70;
      const inTopRight = x > 130 && y < 70;
      const inBottomLeft = x < 70 && y > 130;
      if (inTopLeft || inTopRight || inBottomLeft) continue;
      if (rand() > 0.55) blocks.push([x, y]);
    }
  }
  return blocks;
})();

const QR_FINDERS = [
  [10, 10],
  [130, 10],
  [10, 130],
];

// Four "L" viewfinder corners framing the graphic, like a camera QR scanner
// overlay — always visible regardless of scan state. While `scanning` is
// true they pulse outward and back in on a loop, in phase with the scan-line
// animation (same duration/easing — see "corner-pulse" in tailwind.config.js);
// otherwise they sit still at their normal, closer-in resting position.
function ScannerCorners({ scanning }) {
  const shared = `absolute w-8 h-8 border-emerald-500 ${scanning ? "motion-safe:animate-corner-pulse" : ""}`;
  return (
    <>
      <span className={`${shared} top-1 left-1 border-t-[3px] border-l-[3px] rounded-tl-lg`} />
      <span className={`${shared} top-1 right-1 border-t-[3px] border-r-[3px] rounded-tr-lg`} />
      <span className={`${shared} bottom-1 left-1 border-b-[3px] border-l-[3px] rounded-bl-lg`} />
      <span className={`${shared} bottom-1 right-1 border-b-[3px] border-r-[3px] rounded-br-lg`} />
    </>
  );
}

// `scanning` toggles the moving scan-line + glow overlay — animated only
// while the USB scanner input is focused ("Ready to scan"); when it loses
// focus the overlay is removed entirely rather than just paused, so the
// graphic sits fully static (only the static viewfinder corners remain).
//
// The corners live on this outer, padded wrapper rather than the QR box
// itself, so they frame the code from just outside its edges instead of
// overlapping it — the padding is the gap between bracket and code.
function QrGraphic({ scanning = false }) {
  return (
    <div className="relative p-3 sm:p-4">
      <div className="relative w-52 h-52 sm:w-64 sm:h-64 overflow-hidden rounded-lg">
        <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
          <rect x="0" y="0" width="200" height="200" fill="white" />
          {QR_FINDERS.map(([x, y]) => (
            <g key={`${x}-${y}`}>
              <rect x={x} y={y} width="60" height="60" fill="none" stroke="#0f1c3f" strokeWidth="8" />
              <rect x={x + 18} y={y + 18} width="24" height="24" fill="#0f1c3f" />
            </g>
          ))}
          {QR_NOISE.map(([x, y], i) => (
            <rect key={i} x={x} y={y} width="10" height="10" fill="#0f1c3f" />
          ))}
        </svg>

        {scanning && (
          // Glow band + crisp line move together (same animate-scan-line
          // keyframe) — mix-blend-screen makes the gradient visually "light up"
          // the dark QR pixels it sweeps over instead of just sitting on top.
          <div className="absolute inset-x-0 h-16 -mt-8 motion-safe:animate-scan-line mix-blend-screen">
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(to bottom, transparent, rgba(16,185,129,0.55), transparent)" }}
            />
            <div
              className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2"
              style={{ background: "#34d399", boxShadow: "0 0 10px 2px rgba(52,211,153,0.9)" }}
            />
          </div>
        )}
      </div>

      <ScannerCorners scanning={scanning} />
    </div>
  );
}

// ── Scan Attendance tab ──────────────────────────────────────────────────────
// USB QR scanners on this setup act as a keyboard (type the code, then send
// Enter) — no camera/scanning library involved. So the only job here is to
// keep one text input focused so the "typed" input always lands somewhere,
// and treat Enter (the input's own form submit) as "a code was scanned".
// USB scanners "type" every character of a code within a few milliseconds of
// each other — far faster than a human ever types. Waiting this long after
// the last keystroke with no new ones is a reliable "the scan is finished"
// signal, so a code auto-processes the instant it's fully read instead of
// waiting on an Enter/Tab suffix the scanner may or may not send.
const SCAN_IDLE_MS = 120;

// The two scan endpoints — both gated by the same "scan_member_qr" permission,
// so the toggle is always shown to anyone who can reach this tab.
const SCAN_MODES = [
  { key: "booking", label: "With Booking", hint: "External members must have a confirmed booking for the current time", scan: scanBookingAttendanceCode },
  { key: "walkin", label: "Without Booking", hint: "Records attendance without checking bookings", scan: scanAttendanceCode },
];
const scanModeLabel = (key) => SCAN_MODES.find((m) => m.key === key)?.label || "—";

function ScanAttendanceTab({ active }) {
  const [scanMode, setScanMode] = useState("walkin");
  const [scanInput, setScanInput] = useState("");
  const [scannerFocused, setScannerFocused] = useState(false);
  const [scanLog, setScanLog] = useState([]);
  const inputRef = useRef(null);
  const idleTimerRef = useRef(null);

  // The single result popup for whichever scan most recently happened —
  // { id, code, scannedAt, phase: "verifying" | "done", ...apiResult }.
  // handleScan below always overwrites this unconditionally on a new scan
  // (satisfying "the current popup should automatically close and display
  // the new scan result"), and the .then/.catch handlers below guard their
  // update with `prev?.id === id` so a slow-to-resolve scan can never
  // clobber a newer one that already replaced it on screen.
  const [resultModal, setResultModal] = useState(null);

  // Auto-dismiss the result popup 3s after it settles (success or failure
  // alike) so a busy operator doesn't have to dismiss it between scans —
  // re-armed on every change since a new scan replaces `resultModal` wholesale
  // (see handleScan) and should get its own full 3s. Skipped during the
  // "verifying" phase, which is only ever replaced by its own resolution.
  useEffect(() => {
    if (!resultModal || resultModal.phase !== "done") return undefined;
    const timer = setTimeout(() => setResultModal(null), 3000);
    return () => clearTimeout(timer);
  }, [resultModal]);

  // Keep the scanner input focused whenever this tab is the visible one, so
  // the operator never has to click into it manually.
  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  // Cancel any pending auto-process if the tab is switched away/unmounted
  // mid-scan.
  useEffect(() => () => clearTimeout(idleTimerRef.current), []);

  const refocus = () => inputRef.current?.focus();

  // useCallback (stable identity) — both the USB input path and the camera
  // decode loop's effect below call this, and the effect lists it as a
  // dependency, so it must not change identity every render.
  const handleScan = useCallback((raw) => {
    const code = raw.trim().toUpperCase();
    if (!code) return;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const scannedAt = new Date().toISOString();

    // Optimistic "pending" row first — the internal-student path calls out to
    // an external SIMS API on the backend and can take a few seconds, so the
    // operator sees the code land immediately instead of the table appearing
    // to ignore the scan.
    setScanLog((prev) => [
      { id, code, mode: scanMode, scanned_at: scannedAt, status: "pending", type: null, name: null, detail: null, message: null },
      ...prev,
    ]);
    // Replaces whatever popup is currently on screen (if any) with a
    // "verifying" state for this scan — the guard in .then/.catch below
    // keeps this from being clobbered by an older in-flight scan resolving
    // after a newer one has already taken over the popup.
    setResultModal({ id, code, scannedAt, phase: "verifying" });

    const scan = SCAN_MODES.find((m) => m.key === scanMode).scan;
    scan(code)
      .then((raw) => {
        // The booking endpoint's external-member responses omit `type`
        // (they're external members by definition) — fill it in so the
        // popup/table render them the same as walk-in scans. The
        // not-found/invalid-format rejections have no member_id, so they stay
        // untyped, matching the walk-in endpoint's behavior.
        const result =
          raw.mode === "booking" && !raw.type && raw.member_id != null ? { ...raw, type: "external_member" } : raw;
        const status = result.status || (result.success ? "confirmed" : "rejected");
        setScanLog((prev) =>
          prev.map((entry) =>
            entry.id === id
              ? {
                  ...entry,
                  status,
                  type: result.type ?? null,
                  name: scanDisplayName(result),
                  detail: scanDisplayDetail(result),
                  message: result.message || null,
                }
              : entry
          )
        );
        setResultModal((prev) => (prev?.id === id ? { ...result, id, code, scannedAt, phase: "done", status } : prev));

        if (result.success) toast.success(result.message || "Attendance recorded.");
        else toast.error(result.message || `Scan for "${code}" was not recorded.`);
      })
      .catch((err) => {
        const message = err?.response?.data?.message || "Could not reach the server.";
        setScanLog((prev) =>
          prev.map((entry) => (entry.id === id ? { ...entry, status: "error", message } : entry))
        );
        setResultModal((prev) =>
          prev?.id === id ? { id, code, scannedAt, phase: "done", status: "error", type: null, message } : prev
        );
        toast.error(err?.response?.data?.message || "Scan failed — could not reach the server.");
      });
  }, [scanMode]);

  // Fires from either path below (idle timer or a manual Enter) — whichever
  // gets there first cancels the other so a code is never processed twice.
  const finalizeScan = (value) => {
    clearTimeout(idleTimerRef.current);
    handleScan(value);
    setScanInput("");
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setScanInput(value);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => finalizeScan(value), SCAN_IDLE_MS);
  };

  // Still handled in case a scanner (or a human, testing manually) does send
  // an Enter — processes immediately instead of waiting out the idle timer.
  const handleSubmit = (e) => {
    e.preventDefault();
    finalizeScan(scanInput);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* Ready to Scan panel — the USB scanner input stays fully functional
            (it must keep focus and capture keystrokes) but sits invisibly over
            this card so the visible surface can show the decorative QR graphic
            and status text instead of a text box. */}
        <div className="lg:col-span-5 xl:col-span-4 bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
            Ready to Scan
          </h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">Show this QR code to the member or student.</p>

          {/* Scan mode switch — picks which endpoint the next scan posts to.
              onMouseDown preventDefault keeps focus on the hidden scanner
              input, so switching never interrupts the USB scanner. */}
          <div role="radiogroup" aria-label="Scan mode" className="grid grid-cols-2 gap-1 p-1 mb-2 rounded-xl bg-gray-100">
            {SCAN_MODES.map((m) => {
              const selected = scanMode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setScanMode(m.key);
                    refocus();
                  }}
                  className={`rounded-lg py-2 text-sm font-semibold transition ${
                    selected ? "bg-white shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                  style={selected ? { color: TOKENS.NAVY } : undefined}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mb-4">{SCAN_MODES.find((m) => m.key === scanMode).hint}</p>

          <form onSubmit={handleSubmit}>
            <div
              className={`relative rounded-2xl border-2 border-dashed p-6 flex flex-col items-center justify-center text-center transition ${
                scannerFocused ? "border-blue-300 bg-blue-50/40" : "border-gray-200"
              }`}
            >
              <QrGraphic scanning={scannerFocused} />
              <p className="mt-4 font-bold text-lg" style={{ color: TOKENS.NAVY }}>
                {scannerFocused ? "Ready to scan" : "Not focused"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {scannerFocused ? "Present this card to the USB QR scanner" : "Click to resume scanning"}
              </p>

              <input
                ref={inputRef}
                value={scanInput}
                onChange={handleChange}
                onFocus={() => setScannerFocused(true)}
                onBlur={() => {
                  setScannerFocused(false);
                  // The physical scanner just "types" into whatever has focus —
                  // pull focus back shortly after any incidental click so it
                  // keeps working without the operator touching the keyboard.
                  setTimeout(refocus, 150);
                }}
                onClick={refocus}
                autoFocus
                autoComplete="off"
                aria-label="USB scanner input"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          </form>
        </div>

        <div className="lg:col-span-7 xl:col-span-8 bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm" style={{ color: TOKENS.NAVY }}>
              Scanned This Session ({scanLog.length})
            </h3>
            {scanLog.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    downloadPdf(
                      `scan-attendance-${new Date().toISOString().slice(0, 10)}.pdf`,
                      "Scan Attendance — This Session",
                      ["Code", "Mode", "Resolved To", "Type", "Status", "Scanned At"],
                      scanLog.map((s) => [
                        s.code,
                        scanModeLabel(s.mode),
                        s.name || "",
                        scanTypeLabel(s.type),
                        scanStatusLabel(s.status),
                        fmtTime(s.scanned_at),
                      ])
                    )
                  }
                  className={`${btnSecondary} !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5`}
                >
                  <Icon path={ICONS.download} className="w-3.5 h-3.5" width={2} />
                  Download Attendance Sheet
                </button>
                <button onClick={() => setScanLog([])} className={`${btnSecondary} !py-1.5 !px-3 text-xs`}>
                  Clear session log
                </button>
              </div>
            )}
          </div>
          {scanLog.length === 0 ? (
            <EmptyState icon={ICONS.attendance} text="No scans yet — scan a QR code to see it appear here." />
          ) : (
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-left min-w-[800px]">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-3 font-medium">Code</th>
                    <th className="py-2 px-3 font-medium">Mode</th>
                    <th className="py-2 px-3 font-medium">Resolved To</th>
                    <th className="py-2 px-3 font-medium">Type</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    <th className="py-2 pl-3 font-medium">Scanned At</th>
                  </tr>
                </thead>
                <tbody>
                  {scanLog.map((s) => (
                    <tr key={s.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-3 pr-3 text-sm text-gray-600 font-mono whitespace-nowrap">{s.code}</td>
                      <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{scanModeLabel(s.mode)}</td>
                      <td className="py-3 px-3">
                        <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                          {s.name || "—"}
                        </p>
                        {s.detail && <p className="text-xs text-gray-400 truncate">{s.detail}</p>}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{scanTypeLabel(s.type)}</td>
                      <td className="py-3 px-3">
                        <Pill>{scanStatusLabel(s.status)}</Pill>
                        {s.message && s.status !== "confirmed" && (
                          <p className="text-xs text-gray-400 mt-1 max-w-[220px]">{s.message}</p>
                        )}
                      </td>
                      <td className="py-3 pl-3 text-sm text-gray-600 whitespace-nowrap">{fmtTime(s.scanned_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ScanResultModal result={resultModal} onClose={() => setResultModal(null)} />
    </div>
  );
}

// View-only history tabs: gated by "view_attendance" on the backend. Three
// distinct attendance logs, each its own table (no shared row shape):
//   - External Members (Bookings) — GET /api/external-member-attendances,
//     SFMIS members who scanned in against a facility booking.
//   - External Members (Walk-in) — GET /api/attendance-without-booking/external-members,
//     SFMIS members who scanned in via the Scan Attendance tab with no
//     booking (a row in attendance_without_booking). member_email/
//     primary_phone_number come back null when person_identifier isn't a
//     resolvable member id.
//   - Internal Students — GET /api/internal-student-attendances, university
//     students who scanned in without an SFMIS membership (same
//     attendance_without_booking table as the walk-in tab above, filtered to
//     the other person type).
// All three support a `date` filter server-side; none supports free-text
// search, so the search box below filters the already-fetched rows
// client-side.
//
// The "Scan Attendance" tab (see ScanAttendanceTab above) is a separate flow
// gated by "scan_member_qr" — a With/Without Booking switch picks between
// POST /api/attendance/scan and POST /api/attendance-without-booking/scan.
// Both resolve + record against either an internal student or an external
// member and always return a shaped result, even on rejection. "approve_entry"
// and "deny_entry" are still seeded permissions with no backend route at all yet.
export default function AttendanceManagement() {
  const [activeTab, setActiveTab] = useState("scan"); // "scan" | "external" | "walkin" | "internal"
  const [date, setDate] = useState("");
  const [search, setSearch] = useState("");

  const [externalRecords, setExternalRecords] = useState([]);
  const [externalLoading, setExternalLoading] = useState(true);
  const [walkinRecords, setWalkinRecords] = useState([]);
  const [walkinLoading, setWalkinLoading] = useState(true);
  const [internalRecords, setInternalRecords] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);

  // Clock for "Currently inside" rows' running duration — ticks only while a
  // history tab is open, so the live scan tab isn't re-rendered for nothing.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (activeTab === "scan") return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [activeTab]);

  const fetchExternal = useCallback(() => {
    setExternalLoading(true);
    const params = {};
    if (date) params.date = date;
    listExternalMemberAttendances(params)
      .then(setExternalRecords)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load external member attendance."))
      .finally(() => setExternalLoading(false));
  }, [date]);

  const fetchWalkin = useCallback(() => {
    setWalkinLoading(true);
    const params = {};
    if (date) params.date = date;
    listExternalMembersWithoutBooking(params)
      .then(setWalkinRecords)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load walk-in member attendance."))
      .finally(() => setWalkinLoading(false));
  }, [date]);

  const fetchInternal = useCallback(() => {
    setInternalLoading(true);
    const params = {};
    if (date) params.date = date;
    listInternalStudentAttendances(params)
      .then(setInternalRecords)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load internal student attendance."))
      .finally(() => setInternalLoading(false));
  }, [date]);

  useEffect(() => {
    if (activeTab === "external") fetchExternal();
    else if (activeTab === "walkin") fetchWalkin();
    else if (activeTab === "internal") fetchInternal();
  }, [activeTab, fetchExternal, fetchWalkin, fetchInternal]);

  // Neither history endpoint has a free-text search param — filter the
  // already-fetched, real rows client-side instead of adding one the API
  // doesn't support.
  const visibleExternal = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return externalRecords;
    return externalRecords.filter(
      (a) => memberName(a).toLowerCase().includes(q) || String(a.member_email || "").toLowerCase().includes(q)
    );
  }, [externalRecords, search]);

  const visibleWalkin = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return walkinRecords;
    return walkinRecords.filter(
      (a) =>
        String(a.member_code || "").toLowerCase().includes(q) ||
        String(a.member_email || "").toLowerCase().includes(q) ||
        String(a.primary_phone_number || "").toLowerCase().includes(q)
    );
  }, [walkinRecords, search]);

  const visibleInternal = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return internalRecords;
    return internalRecords.filter(
      (a) =>
        studentName(a).toLowerCase().includes(q) ||
        String(a.registration_no || "").toLowerCase().includes(q) ||
        String(a.faculty || "").toLowerCase().includes(q)
    );
  }, [internalRecords, search]);

  // Client-side pagination — none of the three history endpoints return
  // page/per_page metadata (see attendanceService.js), so this slices the
  // already search/date-filtered arrays above. resetKey includes activeTab
  // so switching tabs (or changing search/date) always lands back on page 1.
  const externalPage = usePagination(visibleExternal, { pageSize: DEFAULT_PAGE_SIZE, resetKey: `${activeTab}|${search}|${date}` });
  const walkinPage = usePagination(visibleWalkin, { pageSize: DEFAULT_PAGE_SIZE, resetKey: `${activeTab}|${search}|${date}` });
  const internalPage = usePagination(visibleInternal, { pageSize: DEFAULT_PAGE_SIZE, resetKey: `${activeTab}|${search}|${date}` });

  const tabs = [
    { key: "scan", label: "Scan Attendance" },
    { key: "external", label: "External Members (Bookings)" },
    { key: "walkin", label: "External Members (Walk-in)" },
    { key: "internal", label: "Internal Students" },
  ];

  // Exports whichever history tab is currently active/filtered — the sheet
  // matches exactly what's on screen (search + date filters already applied).
  const handleDownloadSheet = () => {
    const today = new Date().toISOString().slice(0, 10);
    if (activeTab === "external") {
      downloadPdf(
        `external-member-attendance-${today}.pdf`,
        "External Member Attendance (Bookings)",
        ["Member", "Email", "Booking Date", "Check-in", "Check-out", "Duration", "Scanned Code"],
        visibleExternal.map((a) => [memberName(a), a.member_email, a.booking_date, ...visitText(a, now), a.scanned_code])
      );
    } else if (activeTab === "walkin") {
      downloadPdf(
        `external-member-walkin-attendance-${today}.pdf`,
        "External Member Attendance (Walk-in)",
        ["Member Code", "Email", "Phone", "Status", "Scanned By", "Check-in", "Check-out", "Duration"],
        visibleWalkin.map((a) => [
          a.member_code,
          a.member_email,
          a.primary_phone_number,
          a.status,
          a.scanned_by_name,
          ...visitText(a, now),
        ])
      );
    } else {
      downloadPdf(
        `internal-student-attendance-${today}.pdf`,
        "Internal Student Attendance",
        ["Student", "Registration No.", "Faculty", "Year", "Check-in", "Check-out", "Duration"],
        visibleInternal.map((a) => [studentName(a), a.registration_no, a.faculty, a.year, ...visitText(a, now)])
      );
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Attendance
        </h1>
        <p className="text-sm text-gray-500">
          Scan check-ins with the USB QR scanner, and review external (SFMIS) member (booking and walk-in) and
          internal university student attendance history.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === t.key ? { color: TOKENS.PRIMARY } : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "scan" ? (
        <ScanAttendanceTab active={activeTab === "scan"} />
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder={
                  activeTab === "external"
                    ? "Filter loaded rows by name or email…"
                    : activeTab === "walkin"
                    ? "Filter loaded rows by member code, email or phone…"
                    : "Filter loaded rows by name, registration no. or faculty…"
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <input type="date" className={`${inputClass} w-auto`} value={date} onChange={(e) => setDate(e.target.value)} />
            {date && (
              <button onClick={() => setDate("")} className="text-xs font-semibold text-gray-500 hover:text-gray-800">
                Clear date
              </button>
            )}
            <button
              onClick={handleDownloadSheet}
              disabled={
                (activeTab === "external" ? visibleExternal : activeTab === "walkin" ? visibleWalkin : visibleInternal)
                  .length === 0
              }
              className={`${btnSecondary} inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
              Download Attendance Sheet
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            {activeTab === "external" ? (
              externalLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Spinner />
                  <p className="text-sm text-gray-400">Loading external member attendance…</p>
                </div>
              ) : visibleExternal.length === 0 ? (
                <EmptyState icon={ICONS.attendance} text="No external member attendance records found." />
              ) : (
                <>
                  <div className="overflow-x-auto -mx-5 px-5">
                    <table className="w-full text-left min-w-[900px]">
                      <thead>
                        <tr className="text-xs text-gray-400 border-b border-gray-100">
                          <th className="py-2 pr-3 font-medium">Member</th>
                          <th className="py-2 px-3 font-medium">Booking Date</th>
                          <th className="py-2 px-3 font-medium">Check-in</th>
                          <th className="py-2 px-3 font-medium">Check-out</th>
                          <th className="py-2 px-3 font-medium">Duration</th>
                          <th className="py-2 pl-3 font-medium">Scanned Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {externalPage.pageItems.map((a) => (
                          <tr key={a.id} className="border-b border-gray-50 last:border-0">
                            <td className="py-3 pr-3">
                              <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                                {memberName(a)}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{a.member_email}</p>
                            </td>
                            <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{a.booking_date || "—"}</td>
                            <VisitCells row={a} now={now} />
                            <td className="py-3 pl-3 text-sm text-gray-600 font-mono">{a.scanned_code || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={externalPage.page}
                    totalPages={externalPage.totalPages}
                    onPageChange={externalPage.setPage}
                    className="pt-4"
                  />
                </>
              )
            ) : activeTab === "walkin" ? (
              walkinLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Spinner />
                  <p className="text-sm text-gray-400">Loading walk-in member attendance…</p>
                </div>
              ) : visibleWalkin.length === 0 ? (
                <EmptyState icon={ICONS.attendance} text="No walk-in external member attendance records found." />
              ) : (
                <>
                  <div className="overflow-x-auto -mx-5 px-5">
                    <table className="w-full text-left min-w-[900px]">
                      <thead>
                        <tr className="text-xs text-gray-400 border-b border-gray-100">
                          <th className="py-2 pr-3 font-medium">Member</th>
                          <th className="py-2 px-3 font-medium">Status</th>
                          <th className="py-2 px-3 font-medium">Scanned By</th>
                          <th className="py-2 px-3 font-medium">Check-in</th>
                          <th className="py-2 px-3 font-medium">Check-out</th>
                          <th className="py-2 px-3 font-medium">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {walkinPage.pageItems.map((a) => (
                          <tr key={a.id} className="border-b border-gray-50 last:border-0">
                            <td className="py-3 pr-3">
                              <p className="text-sm font-semibold font-mono" style={{ color: TOKENS.NAVY }}>
                                {a.member_code || "—"}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {[a.member_email, a.primary_phone_number].filter(Boolean).join(" · ") || "—"}
                              </p>
                            </td>
                            <td className="py-3 px-3">
                              <Pill>{a.status}</Pill>
                            </td>
                            <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{a.scanned_by_name || "—"}</td>
                            <VisitCells row={a} now={now} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={walkinPage.page}
                    totalPages={walkinPage.totalPages}
                    onPageChange={walkinPage.setPage}
                    className="pt-4"
                  />
                </>
              )
            ) : internalLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading internal student attendance…</p>
              </div>
            ) : visibleInternal.length === 0 ? (
              <EmptyState icon={ICONS.attendance} text="No internal student attendance records found." />
            ) : (
              <>
                <div className="overflow-x-auto -mx-5 px-5">
                  <table className="w-full text-left min-w-[900px]">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-gray-100">
                        <th className="py-2 pr-3 font-medium">Student</th>
                        <th className="py-2 px-3 font-medium">Registration No.</th>
                        <th className="py-2 px-3 font-medium">Faculty</th>
                        <th className="py-2 px-3 font-medium">Year</th>
                        <th className="py-2 px-3 font-medium">Check-in</th>
                        <th className="py-2 px-3 font-medium">Check-out</th>
                        <th className="py-2 px-3 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {internalPage.pageItems.map((a) => (
                        <tr key={a.id} className="border-b border-gray-50 last:border-0">
                          <td className="py-3 pr-3 text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                            {studentName(a)}
                          </td>
                          <td className="py-3 px-3 text-sm text-gray-600 font-mono whitespace-nowrap">{a.registration_no || "—"}</td>
                          <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{a.faculty || "—"}</td>
                          <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{a.year || "—"}</td>
                          <VisitCells row={a} now={now} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={internalPage.page}
                  totalPages={internalPage.totalPages}
                  onPageChange={internalPage.setPage}
                  className="pt-4"
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
